import { useState, useEffect, useMemo } from "react";
import { useAuth, useUser } from "@clerk/react";
import { useNavigate, Link } from "@tanstack/react-router";
import type { SourceConfig, DisplayMode } from "@tuit/shared-types";
import { adminListSources, adminCreateSource, adminDeleteSource } from "../api";
import { TopBar } from "../components/TopBar";
import styles from "../styles/pages/AdminFeedsPage.module.css";
import adminStyles from "../styles/components/AdminPage.module.css";

const DISPLAY_MODES: DisplayMode[] = ["text", "picture", "gallery"];

type SortKey = "id" | "title" | "url" | "refreshIntervalMinutes";
type SortDir = "asc" | "desc";

const EMPTY_FORM = {
  id: "",
  title: "",
  url: "",
  thumbnails: false,
  refreshIntervalMinutes: 360,
  displayMode: "text" as DisplayMode,
};

export function AdminFeedsPage() {
  const { isLoaded, userId } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const role = (user?.publicMetadata as { role?: string } | undefined)?.role;
  const isAdmin = role === "admin" || role === "editor";

  useEffect(() => {
    if (isLoaded && (!userId || !isAdmin)) {
      navigate({ to: "/" });
    }
  }, [isLoaded, userId, isAdmin, navigate]);

  const [sources, setSources] = useState<SourceConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("title");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [deleting, setDeleting] = useState<Set<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  useEffect(() => {
    loadSources();
  }, []);

  async function loadSources() {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await adminListSources();
      setSources(data);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? sources.filter((s) => s.id.toLowerCase().includes(q) || s.title.toLowerCase().includes(q) || s.url.toLowerCase().includes(q))
      : sources;

    return [...base].sort((a, b) => {
      const av = String(a[sortKey] ?? "");
      const bv = String(b[sortKey] ?? "");
      const cmp = sortKey === "refreshIntervalMinutes"
        ? (a.refreshIntervalMinutes - b.refreshIntervalMinutes)
        : av.localeCompare(bv);
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [sources, search, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function sortArrow(key: SortKey) {
    if (sortKey !== key) return " ↕";
    return sortDir === "asc" ? " ↑" : " ↓";
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreateError(null);
    setCreating(true);
    try {
      const created = await adminCreateSource({
        id: form.id.trim(),
        title: form.title.trim(),
        url: form.url.trim(),
        thumbnails: form.thumbnails,
        refreshIntervalMinutes: form.refreshIntervalMinutes,
        displayMode: form.displayMode,
      });
      setSources((prev) => [...prev, created].sort((a, b) => a.title.localeCompare(b.title)));
      setForm(EMPTY_FORM);
      setShowCreate(false);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Create failed");
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(id: string) {
    setConfirmDelete(null);
    setDeleting((prev) => new Set(prev).add(id));
    try {
      await adminDeleteSource(id);
      setSources((prev) => prev.filter((s) => s.id !== id));
    } finally {
      setDeleting((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  if (!isLoaded || !userId || !isAdmin) return null;

  return (
    <div className={styles.page}>
      <TopBar />
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Link to="/admin" className={styles.back}>← Admin</Link>
            <h1 className={styles.title}>Feeds</h1>
            <span className={styles.count}>{sources.length} sources</span>
          </div>
          <div className={styles.headerRight}>
            <input
              className={styles.searchInput}
              placeholder="Search by ID, title, or URL…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              type="button"
              className={adminStyles.actionBtn}
              onClick={() => { setShowCreate((v) => !v); setCreateError(null); }}
            >
              {showCreate ? "Cancel" : "New Feed"}
            </button>
          </div>
        </div>

        {showCreate && (
          <div className={`${adminStyles.panel} ${styles.createPanel}`}>
            <div className={adminStyles.panelHeader}>
              <h2 className={adminStyles.panelTitle}>New Feed</h2>
            </div>
            <div className={adminStyles.panelBody}>
              <form onSubmit={handleCreate} className={adminStyles.editForm}>
                <div className={styles.createGrid}>
                  <div className={adminStyles.editRow}>
                    <label htmlFor="af-id" className={adminStyles.editLabel}>ID</label>
                    <input
                      id="af-id"
                      className={adminStyles.editInput}
                      placeholder="e.g. my_blog"
                      value={form.id}
                      onChange={(e) => setForm((f) => ({ ...f, id: e.target.value }))}
                      required
                    />
                  </div>
                  <div className={adminStyles.editRow}>
                    <label htmlFor="af-title" className={adminStyles.editLabel}>Title</label>
                    <input
                      id="af-title"
                      className={adminStyles.editInput}
                      placeholder="Display name"
                      value={form.title}
                      onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                      required
                    />
                  </div>
                  <div className={adminStyles.editRow}>
                    <label htmlFor="af-url" className={adminStyles.editLabel}>RSS URL</label>
                    <input
                      id="af-url"
                      className={adminStyles.editInput}
                      type="url"
                      placeholder="https://…"
                      value={form.url}
                      onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                      required
                    />
                  </div>
                  <div className={adminStyles.editRow}>
                    <label htmlFor="af-refresh" className={adminStyles.editLabel}>Refresh (min)</label>
                    <input
                      id="af-refresh"
                      className={adminStyles.editInput}
                      type="number"
                      min={1}
                      value={form.refreshIntervalMinutes}
                      onChange={(e) => setForm((f) => ({ ...f, refreshIntervalMinutes: Number(e.target.value) }))}
                      required
                    />
                  </div>
                  <div className={adminStyles.editRow}>
                    <label htmlFor="af-thumbs" className={adminStyles.editLabel}>Thumbnails</label>
                    <input
                      id="af-thumbs"
                      type="checkbox"
                      checked={form.thumbnails}
                      onChange={(e) => setForm((f) => ({ ...f, thumbnails: e.target.checked }))}
                    />
                  </div>
                  <div className={adminStyles.editRow}>
                    <span className={adminStyles.editLabel}>Display Mode</span>
                    <div className={styles.displayModeToggle}>
                      {DISPLAY_MODES.map((m) => (
                        <button
                          key={m}
                          type="button"
                          className={`${styles.displayModeBtn} ${form.displayMode === m ? styles.displayModeBtnActive : ""}`}
                          onClick={() => setForm((f) => ({ ...f, displayMode: m }))}
                        >
                          {m.charAt(0).toUpperCase() + m.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                {createError && <div className={adminStyles.opsResultError}>{createError}</div>}
                <div className={adminStyles.editActions}>
                  <button type="submit" className={adminStyles.actionBtn} disabled={creating}>
                    {creating ? "Creating…" : "Create Feed"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className={adminStyles.panel}>
          <div className={adminStyles.panelHeader}>
            <h2 className={adminStyles.panelTitle}>
              All Sources
              <span className={adminStyles.panelTitleMeta}>{filtered.length} shown</span>
            </h2>
          </div>
          <div className={styles.tableWrapper}>
            {loading ? (
              <div className={styles.tableMsg}>Loading…</div>
            ) : loadError ? (
              <div className={styles.tableMsg + " " + styles.tableMsgError}>{loadError}</div>
            ) : filtered.length === 0 ? (
              <div className={styles.tableMsg}>
                {search ? "No sources match your search." : "No sources yet. Run Initialize Sources from the admin page first."}
              </div>
            ) : (
              <table className={adminStyles.table}>
                <thead>
                  <tr>
                    <th
                      className={`${adminStyles.thSortable} ${sortKey === "id" ? adminStyles.thActive : ""}`}
                      onClick={() => toggleSort("id")}
                    >
                      ID<span className={adminStyles.sortArrow}>{sortArrow("id")}</span>
                    </th>
                    <th
                      className={`${adminStyles.thSortable} ${sortKey === "title" ? adminStyles.thActive : ""}`}
                      onClick={() => toggleSort("title")}
                    >
                      Title<span className={adminStyles.sortArrow}>{sortArrow("title")}</span>
                    </th>
                    <th
                      className={`${adminStyles.thSortable} ${sortKey === "url" ? adminStyles.thActive : ""}`}
                      onClick={() => toggleSort("url")}
                    >
                      URL<span className={adminStyles.sortArrow}>{sortArrow("url")}</span>
                    </th>
                    <th>Thumbs</th>
                    <th
                      className={`${adminStyles.thSortable} ${sortKey === "refreshIntervalMinutes" ? adminStyles.thActive : ""}`}
                      onClick={() => toggleSort("refreshIntervalMinutes")}
                    >
                      Refresh<span className={adminStyles.sortArrow}>{sortArrow("refreshIntervalMinutes")}</span>
                    </th>
                    <th>Mode</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((source) => (
                    <tr key={source.id}>
                      <td>
                        <code className={styles.idCell}>{source.id}</code>
                      </td>
                      <td>{source.title}</td>
                      <td>
                        <a
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.urlCell}
                          title={source.url}
                        >
                          {source.url}
                        </a>
                      </td>
                      <td className={adminStyles.tdNarrowCenter}>
                        {source.thumbnails ? "✓" : "—"}
                      </td>
                      <td className={adminStyles.tdNarrow}>{source.refreshIntervalMinutes}m</td>
                      <td className={adminStyles.tdNarrow}>{source.displayMode ?? "text"}</td>
                      <td className={adminStyles.tdNarrow}>
                        <div className={styles.rowActions}>
                          <Link
                            to="/admin/feeds/$sourceId"
                            params={{ sourceId: source.id }}
                            className={styles.editLink}
                          >
                            Edit
                          </Link>
                          {confirmDelete === source.id ? (
                            <>
                              <button
                                type="button"
                                className={styles.confirmBtn}
                                disabled={deleting.has(source.id)}
                                onClick={() => handleDelete(source.id)}
                              >
                                {deleting.has(source.id) ? "Deleting…" : "Confirm"}
                              </button>
                              <button
                                type="button"
                                className={styles.cancelBtn}
                                onClick={() => setConfirmDelete(null)}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className={styles.deleteBtn}
                              disabled={deleting.has(source.id)}
                              onClick={() => setConfirmDelete(source.id)}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
